import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Save, Network, FileUp, Pencil, MapPin } from 'lucide-react';
import { LocationImportModal } from '../electrical-systems/shared/LocationImportModal';
import { EditLocationsModal } from '../electrical-systems/shared/EditLocationsModal';
import { FloorPlanManager } from '../electrical-systems/shared/FloorPlanManager';
import { FieldCell } from '../electrical-systems/shared/FieldCell';

const API_BASE = '/communication-systems';

export const CommunicationGeneralInfoSection = ({ projectId, canEditCSIItems, onDirtyChange }) => {
  const [form, setForm] = useState({ systemName: '', description: '', standards: '', designNotes: '' });
  const [savedForm, setSavedForm] = useState(null);
  const [floorPlan, setFloorPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [locations, setLocations] = useState([]);
  const [editLocationsOpen, setEditLocationsOpen] = useState(false);

  const isDirty = useMemo(() => {
    if (!editMode || !savedForm) return false;
    return JSON.stringify(form) !== JSON.stringify(savedForm);
  }, [form, savedForm, editMode]);

  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const fetchLocations = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}${API_BASE}/${projectId}/locations`);
      setLocations(res.data);
    } catch { /* silent */ }
  }, [projectId]);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}${API_BASE}/${projectId}/general-info`);
      const d = res.data;
      if (d && Object.keys(d).length > 0) {
        const loaded = {
          systemName: d.systemName || '', description: d.description || '',
          standards: d.standards || '', designNotes: d.designNotes || '',
        };
        setForm(loaded);
        setSavedForm(loaded);
        setFloorPlan(d.floorPlan || null);
      }
    } catch {
      toast.error('Failed to load general information');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); fetchLocations(); }, [fetchData, fetchLocations]);

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}${API_BASE}/${projectId}/general-info`, form);
      toast.success('General information saved');
      setSavedForm({ ...form });
      setEditMode(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (savedForm) setForm({ ...savedForm });
    setEditMode(false);
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200" data-testid="comm-general-info-section">
      <div className="px-5 py-5 space-y-6">
        {/* Section 1: Communication System Information */}
        <fieldset data-testid="section-comm-system-info">
          <div className="flex items-center justify-between mb-3">
            <legend className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <Network className="h-3.5 w-3.5 text-orange-500" />Communication System Information
            </legend>
            {canEditCSIItems && !editMode && (
              <Button size="sm" onClick={() => setEditMode(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="comm-general-info-edit-btn">
                <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
              </Button>
            )}
            {canEditCSIItems && editMode && (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleCancelEdit} data-testid="comm-general-info-cancel-btn">Cancel</Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="comm-general-info-save-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                  Save Changes
                </Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <FieldCell label="System Name" value={form.systemName} editMode={editMode}>
              <Input value={form.systemName} onChange={e => handleChange('systemName', e.target.value)} placeholder="Enter system name" data-testid="input-systemName" />
            </FieldCell>
            <FieldCell label="Standards" value={form.standards} editMode={editMode}>
              <Input value={form.standards} onChange={e => handleChange('standards', e.target.value)} placeholder="e.g. TIA-568, ISO/IEC 11801" data-testid="input-standards" />
            </FieldCell>
          </div>
          <div className="grid grid-cols-1 gap-y-2 mt-2">
            <FieldCell label="Description" value={form.description} editMode={editMode}>
              <Textarea value={form.description} onChange={e => handleChange('description', e.target.value)} placeholder="Describe the communication system..." rows={3} data-testid="input-description" />
            </FieldCell>
            <FieldCell label="Design Notes" value={form.designNotes} editMode={editMode}>
              <Textarea value={form.designNotes} onChange={e => handleChange('designNotes', e.target.value)} placeholder="Additional design notes..." rows={3} data-testid="input-designNotes" />
            </FieldCell>
          </div>
        </fieldset>

        <hr className="border-slate-100" />

        {/* Section 2: Locations */}
        <div className="flex flex-col" data-testid="section-locations">
          <div className="flex items-center justify-between mb-3">
            <span className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <MapPin className="h-3.5 w-3.5 text-orange-500" />Locations
            </span>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditLocationsOpen(true)} data-testid="edit-locations-btn">
                <Pencil className="h-3 w-3 mr-1" />Edit
              </Button>
              <LocationImportModal projectId={projectId} onImportComplete={fetchLocations} apiBase={API_BASE} />
            </div>
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            {locations.length > 0 ? (
              <div className="max-h-52 overflow-y-auto" data-testid="locations-list">
                {locations.map((loc, i) => (
                  <div key={loc.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                    <MapPin className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                    <span className="text-slate-700">{loc.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-24 text-sm text-slate-400 italic" data-testid="locations-empty">
                No locations added
              </div>
            )}
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">{locations.length} location{locations.length !== 1 ? 's' : ''} — used across Communication sub-tabs</p>
        </div>

        <hr className="border-slate-100" />

        {/* Section 3: Floor Plan */}
        <fieldset data-testid="section-floor-plan">
          <legend className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            <FileUp className="h-3.5 w-3.5 text-orange-500" />Floor Plan
          </legend>
          <FloorPlanManager
            apiBase={API_BASE}
            projectId={projectId}
            floorPlan={floorPlan}
            onChange={setFloorPlan}
            editMode={editMode}
          />
        </fieldset>
      </div>

      <EditLocationsModal
        open={editLocationsOpen}
        onOpenChange={setEditLocationsOpen}
        projectId={projectId}
        onLocationsChanged={fetchLocations}
        apiBase={API_BASE}
      />
    </div>
  );
};
